import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { ListOrganizationDealPerFrameResponse } from '@enechain/eposition-proto/bffportal/v1/report_service_pb'
import { useErrorToast, useSuccessToast } from '@enechain/eposition-ui'
import {
  createAndDownloadZip,
  downloadCSVFromString,
} from '@enechain/eposition-utils'

import { ContractListFilterState } from '~/pages/ContractList/hooks/useContractListFilter'
import {
  ContractDealReportModel,
  getExportPeriod,
} from '~/pages/ContractList/models/contractDealReport'
import type { DealReportCsvRow } from '~/pages/ContractList/types/types'
import { generateContractExportFileName } from '~/pages/ContractList/utils/contractExportFileNameGenerator'
import {
  generateDealReportCsvString,
  generatePerFrameContractCsvRows,
  splitCsvRowsByArea,
} from '~/pages/ContractList/utils/perFrameContractReporCSVGenerator'

type UsePerFrameContractCsvReportExportReturn = {
  exportCsv: (options?: { splitArea?: boolean }) => Promise<void>
  isLoading: boolean
}

export function usePerFrameContractCsvReportExport(
  perFrameData: ListOrganizationDealPerFrameResponse | undefined,
  contractData: ContractDealReportModel[],
  filterState: ContractListFilterState,
): UsePerFrameContractCsvReportExportReturn {
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  const { showToast: showSuccessToast } = useSuccessToast({
    toastId: 'PerFrameContractCsvExport/success',
  })
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'PerFrameContractCsvExport/error',
  })

  async function exportCsv(
    options: { splitArea?: boolean } = {},
  ): Promise<void> {
    const { splitArea = false } = options
    setIsLoading(true)

    try {
      if (perFrameData === undefined) {
        showErrorToast({
          toastMessage: 'No data to export',
        })
        return
      }

      const { fromDate, toDate } = getExportPeriod(contractData, filterState)
      const headerLabels = createCsvHeaderLabels(t)
      await exportReportAsCsv(
        perFrameData,
        headerLabels,
        splitArea,
        fromDate,
        toDate,
      )

      showSuccessToast({
        toastMessage: t('common:success.download'),
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'CSV export failed'

      showErrorToast({
        toastMessage: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    exportCsv,
    isLoading,
  }
}

/**
 * Generate CSV header labels from i18n translations
 */
function createCsvHeaderLabels(
  t: (key: string) => string,
): Record<keyof DealReportCsvRow, string> {
  return {
    date: t('page:report.csv_headers.date'),
    frame: t('page:report.csv_headers.frame'),
    timeFrom: t('page:report.csv_headers.timeFrom'),
    timeTo: t('page:report.csv_headers.timeTo'),
    hokkaidoBid: t('page:report.csv_headers.hokkaidoBid'),
    hokkaidoOffer: t('page:report.csv_headers.hokkaidoOffer'),
    tohokuBid: t('page:report.csv_headers.tohokuBid'),
    tohokuOffer: t('page:report.csv_headers.tohokuOffer'),
    tokyoBid: t('page:report.csv_headers.tokyoBid'),
    tokyoOffer: t('page:report.csv_headers.tokyoOffer'),
    chubuBid: t('page:report.csv_headers.chubuBid'),
    chubuOffer: t('page:report.csv_headers.chubuOffer'),
    hokurikuBid: t('page:report.csv_headers.hokurikuBid'),
    hokurikuOffer: t('page:report.csv_headers.hokurikuOffer'),
    kansaiBid: t('page:report.csv_headers.kansaiBid'),
    kansaiOffer: t('page:report.csv_headers.kansaiOffer'),
    chugokuBid: t('page:report.csv_headers.chugokuBid'),
    chugokuOffer: t('page:report.csv_headers.chugokuOffer'),
    shikokuBid: t('page:report.csv_headers.shikokuBid'),
    shikokuOffer: t('page:report.csv_headers.shikokuOffer'),
    kyushuBid: t('page:report.csv_headers.kyushuBid'),
    kyushuOffer: t('page:report.csv_headers.kyushuOffer'),
  }
}

async function exportReportAsCsv(
  dealData: ListOrganizationDealPerFrameResponse,
  headerLabels: Record<keyof DealReportCsvRow, string>,
  splitArea = false,
  fromDate: Date,
  toDate: Date,
): Promise<void> {
  if (
    dealData == null ||
    dealData.dealsPerFrame == null ||
    dealData.dealsPerFrame.length === 0
  ) {
    throw new Error('No data to export')
  }

  const csvRows = generatePerFrameContractCsvRows(dealData, fromDate, toDate)

  if (csvRows.length === 0) {
    throw new Error('no data to export')
  }

  if (splitArea) {
    const areaFiles = splitCsvRowsByArea(
      csvRows,
      headerLabels,
      fromDate,
      toDate,
    )

    if (areaFiles.size === 0) {
      throw new Error('no area data')
    }

    const filesToZip = Array.from(areaFiles.values())
    const zipFilename = generateContractExportFileName({
      fromDate,
      toDate,
      type: 'PER_FRAME',
      fileType: 'zip',
    })

    createAndDownloadZip(filesToZip, zipFilename)
  } else {
    // Export as single CSV file
    const csvContent = generateDealReportCsvString(csvRows, headerLabels)
    const filename = generateContractExportFileName({
      fromDate,
      toDate,
      type: 'PER_FRAME',
    })

    downloadCSVFromString(csvContent, filename, true)
  }
}
